import {
  ADMIN_SETTINGS_DAILY_SHIFTS,
  BUSINESS_VIEW_NAME,
  CORE_BUSINESS_TYPE_LABEL,
  DAYS,
  MANDATORY_SSD_ADMIN_SETTINGS_KEYS,
  SSD_FIRST_SHIFT_HALF_INDICATOR,
  SSD_SECOND_SHIFT_HALF_INDICATOR,
  SSD_TIMELINE_KEY,
  SSD_TIMELINE_LABEL,
  SSD_TIMELINE_TYPE
} from '../../utils/constants';
import moment from 'moment';
import 'moment-timezone';
import { chainWalk, trimAndCapitalizeFirstLetter } from '../../utils/helpers';
import { getStationCode } from '../../utils/networkUtil';

const TIME_DELIMITER = ':';
const SECONDS_IN_A_MINUTE = 60;

export const getTimelineForViewPlan = (plan) => {
  const adminSettings = chainWalk(() => JSON.parse(plan.input.adminSettings), null);
  const planStartTimeEpoch = plan.shiftPlanHorizonStartTime;
  if (!adminSettings) return [];
  if (!planStartTimeEpoch) return [];
  if (!adminSettings.ops_clock) return [];
  if (!adminSettings.break_config) return [];
  return generateTimeline(planStartTimeEpoch - SECONDS_IN_A_MINUTE, false, adminSettings);
};

export const getTimelineForCreatePlan = (adminSettings, shouldStartAtNextShiftHalf) => {
  if (!adminSettings) return [];
  if (!adminSettings.ops_clock) return [];
  if (!adminSettings.break_config) return [];
  return generateTimeline(Math.floor(new Date().setSeconds(0) / 1000), shouldStartAtNextShiftHalf, adminSettings);
};

export const validateAdminSettings = (adminSettings = {}) => {
  const invalidKeys = [];

  MANDATORY_SSD_ADMIN_SETTINGS_KEYS.forEach((key) => {
    if (
      !(key in adminSettings) ||
      adminSettings[key] === null ||
      adminSettings[key] === undefined ||
      (Array.isArray(adminSettings[key])
        ? adminSettings[key].length === 0
        : JSON.stringify(adminSettings[key]) === '{}')
    ) {
      invalidKeys.push(key);
    }
  });

  return {
    requiredKeysPresent: invalidKeys.length === 0,
    invalidKeys
  };
};

const generateTimeline = (currentTimeEpoch, shouldStartAtNextShiftHalf, adminSettings) => {
  const { shifts, ops_clock: opsClocks, break_config: breakConfigs, timezone = moment.tz.guess() } = adminSettings;
  const currentMoment = moment.unix(currentTimeEpoch).tz(timezone);
  const currentLocalTime = currentMoment.format('HH:mm:ss');
  const nextQuarterLocalTime = getNextQuarterLocalTime(currentLocalTime);
  shifts.sort((s1, s2) => (s1.start > s2.start ? 1 : -1));
  const planStartShift = getStartShiftForTimeline(shouldStartAtNextShiftHalf, nextQuarterLocalTime, shifts);
  const sortedShifts = getShiftsWithOrder(planStartShift, shifts);
  const planStartTimeLocal = planStartShift.start;
  const planStartTimeEpoch = localTimeToEpochFutureReference(currentTimeEpoch, planStartTimeLocal, timezone);
  const { timelineEvents, planEndTimeEpoch } = addPlanAndShiftTimelineEvents(
    planStartShift,
    planStartTimeLocal,
    planStartTimeEpoch,
    sortedShifts,
    timezone
  );
  addCPTTimelineEvents(timelineEvents, opsClocks, planStartTimeLocal, planStartTimeEpoch, timezone);
  addCETTimelineEvents(timelineEvents, opsClocks, planStartTimeLocal, planStartTimeEpoch, planEndTimeEpoch, timezone);
  addBreakTimelineEvents(timelineEvents, breakConfigs, planStartTimeLocal, planStartTimeEpoch, timezone);
  timelineEvents.sort((t1, t2) =>
    chainWalk(() => t1.date_time_local.toString(), '').localeCompare(chainWalk(() => t2.date_time_local.toString(), ''))
  );
  console.log(timelineEvents);
  return timelineEvents;
};

const addPlanAndShiftTimelineEvents = (
  planStartShift,
  planStartLocalTime,
  planStartTimeEpoch,
  sortedShifts,
  timezone
) => {
  const planStartDatetimeLocal = convertEpochToDatetimeLocal(planStartTimeEpoch, timezone);
  const timelineEvents = [];
  timelineEvents.push(
    getTimelineEvent(
      SSD_TIMELINE_KEY.PLAN,
      SSD_TIMELINE_LABEL.PLAN_START,
      SSD_TIMELINE_TYPE.START,
      planStartDatetimeLocal,
      planStartTimeEpoch
    )
  );
  let prevShiftStartTimeEpoch = planStartTimeEpoch;
  sortedShifts.forEach((shift) => {
    const shiftStartTimeLocal = shift.start;
    const shiftEndTimeLocal = shift.end;
    let shiftStartTimeEpoch;
    if (shift.name === planStartShift.name) {
      shiftStartTimeEpoch = localTimeToEpochPastReference(planStartTimeEpoch, shiftStartTimeLocal, timezone);
      prevShiftStartTimeEpoch = shiftStartTimeEpoch;
    } else {
      shiftStartTimeEpoch = localTimeToEpochFutureReference(prevShiftStartTimeEpoch, shiftStartTimeLocal, timezone);
    }
    const shiftStartDatetimeLocal = convertEpochToDatetimeLocal(shiftStartTimeEpoch, timezone);
    const shiftEndTimeEpoch = localTimeToEpochFutureReference(shiftStartTimeEpoch, shiftEndTimeLocal, timezone);
    const shiftEndDatetimeLocal = convertEpochToDatetimeLocal(shiftEndTimeEpoch, timezone);
    timelineEvents.push(
      getTimelineEvent(
        SSD_TIMELINE_KEY.SHIFT,
        shift.label,
        SSD_TIMELINE_TYPE.START,
        shiftStartDatetimeLocal,
        shiftStartTimeEpoch
      )
    );
    timelineEvents.push(
      getTimelineEvent(
        SSD_TIMELINE_KEY.SHIFT,
        shift.label,
        SSD_TIMELINE_TYPE.END,
        shiftEndDatetimeLocal,
        shiftEndTimeEpoch
      )
    );
    const [startHours, startMinutes, _] = shift.start.split(TIME_DELIMITER);
    const [endHours, endMinutes, __] = shift.end.split(TIME_DELIMITER);
    const shiftStartHHMM = startHours + TIME_DELIMITER + startMinutes;
    const shiftEndHHMM = endHours + TIME_DELIMITER + endMinutes;
    const shiftStartToEndString = `${shiftStartHHMM}-${shiftEndHHMM}`;
    const shiftFirstHalfStartTimeEpoch = localTimeToEpochFutureReference(
      prevShiftStartTimeEpoch,
      shift.first_half_start,
      timezone
    );
    const shiftFirstHalfStartDatetimeLocal = convertEpochToDatetimeLocal(shiftFirstHalfStartTimeEpoch, timezone);
    const shiftFirstHalfEndTimeEpoch = localTimeToEpochFutureReference(
      prevShiftStartTimeEpoch,
      shift.first_half_end,
      timezone
    );
    const shiftFirstHalfEndDatetimeLocal = convertEpochToDatetimeLocal(shiftFirstHalfEndTimeEpoch, timezone);
    timelineEvents.push(
      getTimelineEvent(
        SSD_TIMELINE_KEY.SHIFT_HALF,
        `${shift.label} (${shiftStartToEndString}, ${SSD_FIRST_SHIFT_HALF_INDICATOR})`,
        SSD_TIMELINE_TYPE.START,
        shiftFirstHalfStartDatetimeLocal,
        shiftFirstHalfStartTimeEpoch
      )
    );
    timelineEvents.push(
      getTimelineEvent(
        SSD_TIMELINE_KEY.SHIFT_HALF,
        `${shift.label} (${shiftStartToEndString}, ${SSD_FIRST_SHIFT_HALF_INDICATOR})`,
        SSD_TIMELINE_TYPE.END,
        shiftFirstHalfEndDatetimeLocal,
        shiftFirstHalfEndTimeEpoch
      )
    );
    const shiftSecondHalfStartTimeEpoch = localTimeToEpochFutureReference(
      prevShiftStartTimeEpoch,
      shift.back_half_start,
      timezone
    );
    const shiftSecondHalfStartDatetimeLocal = convertEpochToDatetimeLocal(shiftSecondHalfStartTimeEpoch, timezone);
    const shiftSecondHalfEndTimeEpoch = localTimeToEpochFutureReference(
      prevShiftStartTimeEpoch,
      shift.back_half_end,
      timezone
    );
    const shiftSecondHalfEndDatetimeLocal = convertEpochToDatetimeLocal(shiftSecondHalfEndTimeEpoch, timezone);
    timelineEvents.push(
      getTimelineEvent(
        SSD_TIMELINE_KEY.SHIFT_HALF,
        `${shift.label} (${shiftStartToEndString}, ${SSD_SECOND_SHIFT_HALF_INDICATOR})`,
        SSD_TIMELINE_TYPE.START,
        shiftSecondHalfStartDatetimeLocal,
        shiftSecondHalfStartTimeEpoch
      )
    );
    timelineEvents.push(
      getTimelineEvent(
        SSD_TIMELINE_KEY.SHIFT_HALF,
        `${shift.label} (${shiftStartToEndString}, ${SSD_SECOND_SHIFT_HALF_INDICATOR})`,
        SSD_TIMELINE_TYPE.END,
        shiftSecondHalfEndDatetimeLocal,
        shiftSecondHalfEndTimeEpoch
      )
    );
    prevShiftStartTimeEpoch = shiftStartTimeEpoch;
  });
  const planEndShift = sortedShifts[sortedShifts.length - 1];
  const planEndTimeEpoch = localTimeToEpochFutureReference(prevShiftStartTimeEpoch, planEndShift.end, timezone);
  const planEndDatetimeLocal = convertEpochToDatetimeLocal(planEndTimeEpoch, timezone);
  timelineEvents.push(
    getTimelineEvent(
      SSD_TIMELINE_KEY.PLAN,
      SSD_TIMELINE_LABEL.PLAN_END,
      SSD_TIMELINE_TYPE.END,
      planEndDatetimeLocal,
      planEndTimeEpoch
    )
  );
  return {
    timelineEvents,
    planStartTimeEpoch,
    planEndTimeEpoch
  };
};

const getTimelineEvent = (key, label, type, datetimeLocal, timestamp) => {
  return {
    key,
    label,
    type,
    date_time_local: datetimeLocal,
    timestamp
  };
};
const addCPTTimelineEvents = (timelineEvents, opsClocks, planStartLocalTime, planStartTimeEpoch, timezone) => {
  let previousCPTStartTimeEpoch = planStartTimeEpoch;
  const cptOpsClock = opsClocks.find(({ name }) => name === SSD_TIMELINE_KEY.CPT);
  const windowList = cptOpsClock.window_list;
  windowList.sort((w1, w2) => (w1.time > w2.time ? 1 : -1));
  let firstCpt = windowList.find((w) => w.time >= planStartLocalTime);
  if (!firstCpt) {
    firstCpt = windowList[0];
  }
  const index = windowList.findIndex(({ time }) => time === firstCpt.time);
  const toAddLater = windowList.splice(0, index);
  const toAddAtEnd = windowList.length > 0 ? windowList[0] : null;
  let i = 0;
  windowList.forEach((window) => {
    window.order = i;
    i = i + 1;
  });
  toAddLater.forEach((window) => {
    window.order = i;
    i = i + 1;
    windowList.push(window);
  });
  windowList.sort((w1, w2) => w1.order - w2.order);
  windowList.forEach((window) => {
    const cptTimeLocal = window.time;
    const cptTimeEpoch = localTimeToEpochFutureReference(previousCPTStartTimeEpoch, cptTimeLocal, timezone);
    const cptDatetimeLocal = convertEpochToDatetimeLocal(cptTimeEpoch, timezone);
    timelineEvents.push(
      getTimelineEvent(SSD_TIMELINE_KEY.CPT, window.name, SSD_TIMELINE_TYPE.START, cptDatetimeLocal, cptTimeEpoch)
    );
    previousCPTStartTimeEpoch = cptTimeEpoch;
  });
  if (toAddAtEnd && Object.keys(toAddAtEnd).length > 0) {
    const cptTimeLocal = toAddAtEnd.time;
    const cptTimeEpoch = localTimeToEpochFutureReference(previousCPTStartTimeEpoch, cptTimeLocal, timezone);
    const cptDatetimeLocal = convertEpochToDatetimeLocal(cptTimeEpoch, timezone);
    timelineEvents.push(
      getTimelineEvent(SSD_TIMELINE_KEY.CPT, toAddAtEnd.name, SSD_TIMELINE_TYPE.START, cptDatetimeLocal, cptTimeEpoch)
    );
    previousCPTStartTimeEpoch = cptTimeEpoch;
  }
  return timelineEvents;
};
const addCETTimelineEvents = (
  timelineEvents,
  opsClocks,
  planStartLocalTime,
  planStartTimeEpoch,
  planEndTimeEpoch,
  timezone
) => {
  const opsClockForCet1 = opsClocks.find(({ name }) => name === 'cet1');
  const opsClockForCet2 = opsClocks.find(({ name }) => name === 'cet2');
  const cet1WindowList = opsClockForCet1.window_list.filter(({ time }) => !!time);
  const cet2WindowList = opsClockForCet2.window_list.filter(({ time }) => !!time);
  addIndividualCetTimelineEvents(
    timelineEvents,
    cet1WindowList,
    planStartLocalTime,
    planStartTimeEpoch,
    planEndTimeEpoch,
    timezone,
    'cet1'
  );
  addIndividualCetTimelineEvents(
    timelineEvents,
    cet2WindowList,
    planStartLocalTime,
    planStartTimeEpoch,
    planEndTimeEpoch,
    timezone,
    'cet2'
  );
};

const addIndividualCetTimelineEvents = (
  timelineEvents,
  cetWindowList,
  planStartLocalTime,
  planStartTimeEpoch,
  planEndTimeEpoch,
  timezone,
  cetId
) => {
  let previousCetEpoch = planStartTimeEpoch;
  let firstCetIndex = cetWindowList.findIndex(({ time }) => time >= planStartLocalTime);
  if (firstCetIndex < 0) {
    firstCetIndex = 0;
  }
  const toAddLater = cetWindowList.splice(0, firstCetIndex);
  let i = 0;
  cetWindowList.forEach((window) => {
    window.order = i;
    i = i + 1;
  });
  toAddLater.forEach((window) => {
    window.order = i;
    i = i + 1;
    cetWindowList.push(window);
  });
  cetWindowList.sort((w1, w2) => w1.order - w2.order);
  cetWindowList.forEach((cetWindow) => {
    const cetTimeLocal = cetWindow.time;
    const cetTimeEpoch = localTimeToEpochFutureReference(previousCetEpoch, cetTimeLocal, timezone);
    if (cetTimeEpoch > planEndTimeEpoch) {
      return false;
    }

    const cetDatetimeLocal = convertEpochToDatetimeLocal(cetTimeEpoch, timezone);
    timelineEvents.push(getTimelineEvent(SSD_TIMELINE_KEY.CET, cetWindow.name, cetId, cetDatetimeLocal, cetTimeEpoch));
    previousCetEpoch = cetTimeEpoch;
  });
};

const addBreakTimelineEvents = (timelineEvents, breaks, planStartLocalTime, planStartTimeEpoch, timezone) => {
  const previousBreakStartTimeEpoch = planStartTimeEpoch;
  breaks.sort((b1, b2) => (b1.start > b2.start ? 1 : -1));
  let firstBreak = breaks.find(({ break_start_time_local }) => break_start_time_local >= planStartLocalTime);
  if (!firstBreak) {
    firstBreak = breaks[0];
  }
  const index = breaks.findIndex(({ break_group_name }) => break_group_name === firstBreak.break_group_name);
  const toAddLater = breaks.splice(0, index);
  let i = 0;
  breaks.forEach((b) => {
    b.order = i;
    i = i + 1;
  });
  toAddLater.forEach((b) => {
    b.order = i;
    i = i + 1;
    breaks.push(b);
  });
  breaks.sort((b1, b2) => b1.order - b2.order);
  breaks.forEach((b) => {
    const breakStartTimeLocal = b.break_start_time_local;
    const breakStartTimeEpoch = localTimeToEpochFutureReference(
      previousBreakStartTimeEpoch,
      breakStartTimeLocal,
      timezone
    );
    const breakStartDatetimeLocal = convertEpochToDatetimeLocal(breakStartTimeEpoch, timezone);
    timelineEvents.push(
      getTimelineEvent(
        SSD_TIMELINE_KEY.BREAK,
        b.break_group_name,
        SSD_TIMELINE_TYPE.START,
        breakStartDatetimeLocal,
        breakStartTimeEpoch
      )
    );
    const breakEndTimeLocal = b.break_end_time_local;
    const breakEndTimeEpoch = localTimeToEpochFutureReference(previousBreakStartTimeEpoch, breakEndTimeLocal, timezone);
    const breakEndDatetimeLocal = convertEpochToDatetimeLocal(breakEndTimeEpoch, timezone);
    timelineEvents.push(
      getTimelineEvent(
        SSD_TIMELINE_KEY.BREAK,
        b.break_group_name,
        SSD_TIMELINE_TYPE.END,
        breakEndDatetimeLocal,
        breakEndTimeEpoch
      )
    );
  });
};

const localTimeToEpochFutureReference = (referenceEpoch, localTime, timezone) => {
  const m = moment.unix(referenceEpoch).tz(timezone);
  const [hours, minutes, seconds] = localTime.split(':').map((item) => parseInt(item));
  let targetMoment = m
    .clone()
    .hour(hours)
    .minute(minutes)
    .second(seconds || 0);
  if (referenceEpoch > targetMoment.unix()) {
    targetMoment = targetMoment.add(1, 'day');
  }
  return targetMoment.unix();
};

const convertEpochToDatetimeLocal = (epoch, timezone) => {
  return moment.unix(epoch).tz(timezone).format('YYYY-MM-DD[T]HH:mm:SS.[000Z]');
};

const localTimeToEpochPastReference = (referenceEpoch, localTime, timezone) => {
  const m = moment.unix(referenceEpoch).tz(timezone);
  const [hours, minutes, seconds] = localTime.split(':').map((item) => parseInt(item));
  let targetMoment = m
    .clone()
    .hour(hours)
    .minute(minutes)
    .second(seconds || 0);
  if (referenceEpoch < targetMoment.unix()) {
    targetMoment = targetMoment.subtract(1, 'day');
  }
  return targetMoment.unix();
};

const getStartShiftForTimeline = (isStartAtNextShift, nextQuarterLocalTime, shifts) => {
  if (isStartAtNextShift) {
    return getNextShiftOnOrAfterTime(nextQuarterLocalTime, shifts);
  } else {
    return getShiftForTime(nextQuarterLocalTime, shifts);
  }
};

const isAcrossDayShift = (localStartTime, localEndTime) => {
  return localEndTime < localStartTime;
};
const getShiftForTime = (localTime, shifts) => {
  let currentShift;
  for (let shift of shifts) {
    if (
      (localTime >= shift.first_half_start && localTime < shift.first_half_end) ||
      (isAcrossDayShift(shift.first_half_start, shift.first_half_end) &&
        (localTime >= shift.first_half_start || localTime < shift.first_half_end))
    ) {
      currentShift = {
        name: shift.name,
        start: localTime,
        isFirstHalf: true
      };
      break;
    }
    if (
      (localTime >= shift.back_half_start && localTime < shift.back_half_end) ||
      (isAcrossDayShift(shift.back_half_start, shift.back_half_end) &&
        (localTime >= shift.back_half_start || localTime < shift.back_half_end))
    ) {
      currentShift = {
        name: shift.name,
        start: localTime,
        isFirstHalf: false
      };
      break;
    }
  }
  if (currentShift) {
    return currentShift;
  }
  return getNextShiftOnOrAfterTime(localTime, shifts);
};

// converts P1,P2,P3,P4,P5 to P3,P4,P5,P1,P2 if planStartShift is P3
const getShiftsWithOrder = (planStartShift, shifts) => {
  const index = shifts.findIndex((shift) => shift.name === planStartShift.name);
  const toAddLater = shifts.splice(0, index);
  let i = 0;
  shifts.forEach((shift) => {
    shift.order = i;
    i = i + 1;
  });
  toAddLater.forEach((shift) => {
    shift.order = i;
    i = i + 1;
    shifts.push(shift);
  });
  return shifts.sort((s1, s2) => s1.order - s2.order);
};
const getNextShiftOnOrAfterTime = (localTime, shifts) => {
  let nextShift;
  for (let shift of shifts) {
    if (shift.first_half_start >= localTime) {
      nextShift = {
        name: shift.name,
        start: shift.first_half_start,
        isFirstHalf: true
      };
      break;
    }
    if (shift.back_half_start >= localTime) {
      nextShift = {
        name: shift.name,
        start: shift.back_half_start,
        isFirstHalf: false
      };
      break;
    }
  }
  if (nextShift) {
    return nextShift;
  } else {
    return getDefaultShiftDetails(shifts);
  }
};

const getDefaultShiftDetails = (shifts) => {
  const shiftStartTimeLocals = [
    ...shifts.map((shift) => shift.first_half_start),
    ...shifts.map((shift) => shift.back_half_start)
  ].sort();
  const defaultEarliestShiftStartTimeLocal = shiftStartTimeLocals[0];
  let defaultShift = {};
  for (let shift of shifts) {
    if (shift.first_half_start === defaultEarliestShiftStartTimeLocal) {
      defaultShift = {
        name: shift['name'],
        start: shift.first_half_start,
        isFirstHalf: true
      };
      break;
    }
    if (shift.back_half_start === defaultEarliestShiftStartTimeLocal) {
      defaultShift = {
        name: shift['name'],
        start: shift.back_half_start,
        isFirstHalf: false
      };
      break;
    }
  }
  return defaultShift;
};

const getNextQuarterLocalTime = (localTime) => {
  const [hours, minutes, seconds] = localTime.split(':').map(Number);
  const minutesSinceMidnight = hours * 60 + minutes;
  const secondsSinceMidnight = minutesSinceMidnight * 60 + seconds;

  let nextQuarterSeconds = Math.ceil(secondsSinceMidnight / 900) * 900;
  if (nextQuarterSeconds >= 86400) {
    nextQuarterSeconds = 0;
  }

  const nextHours = Math.floor(nextQuarterSeconds / 3600);
  const nextMinutes = Math.floor((nextQuarterSeconds % 3600) / 60);

  return ('0' + nextHours).slice(-2) + ':' + ('0' + nextMinutes).slice(-2) + ':' + '00';
};

export const getShiftTimeline = (shift, timeline) => {
  if (!shift) {
    return [];
  }
  return timeline.filter(
    ({ date_time_local, type, key }) =>
      date_time_local >= shift.start &&
      date_time_local <= shift.end &&
      (type === SSD_TIMELINE_TYPE.START || (type === SSD_TIMELINE_TYPE.END && key === SSD_TIMELINE_KEY.PLAN))
  );
};

export const getBreaks = (timeline) => {
  return timeline
    .filter(({ key, type }) => key === SSD_TIMELINE_KEY.BREAK && type === SSD_TIMELINE_TYPE.START)
    .map((breakStart) => {
      const breakEnd = timeline.find(
        ({ key, type, label }) =>
          key === SSD_TIMELINE_KEY.BREAK && type === SSD_TIMELINE_TYPE.END && label === breakStart.label
      );
      return {
        label: breakStart.label,
        key: breakStart.key,
        start: breakStart.date_time_local,
        start_timestamp: breakStart.timestamp,
        end: breakEnd.date_time_local,
        end_timestamp: breakEnd.timestamp
      };
    });
};

export const isBreakQuarter = (quarter, breaks) => {
  return breaks.filter((b) => quarter >= b.start && quarter < b.end).length > 0;
};

export const getShifts = (timeline) => {
  return timeline
    .filter(({ key, type }) => key === SSD_TIMELINE_KEY.SHIFT && type === SSD_TIMELINE_TYPE.START)
    .map((shiftStart) => {
      const shiftEnd = timeline.find(
        ({ key, type, label }) =>
          key === SSD_TIMELINE_KEY.SHIFT && type === SSD_TIMELINE_TYPE.END && label === shiftStart.label
      );
      return {
        label: shiftStart.label,
        key: shiftStart.key,
        start: shiftStart.date_time_local,
        start_timestamp: shiftStart.timestamp,
        end: shiftEnd.date_time_local,
        end_timestamp: shiftEnd.timestamp
      };
    });
};

export const getShiftSchedules = (sortedTimeline) => {
  const shiftHalves = getShiftHalves(sortedTimeline);
  const shifts = getShiftObjs(sortedTimeline, SSD_TIMELINE_KEY.SHIFT);
  return shifts.map((shift) => {
    const firstHalf = shiftHalves.find((shiftHalf) => shiftHalf.startTimestamp === shift.startTimestamp);
    const secondHalf = shiftHalves.find((shiftHalf) => shiftHalf.endTimestamp === shift.endTimestamp);
    return {
      ...shift,
      firstHalf,
      secondHalf
    };
  });
};

export const getShiftHalves = (sortedTimeline) => {
  return getShiftObjs(sortedTimeline, SSD_TIMELINE_KEY.SHIFT_HALF);
};

const getShiftObjs = (sortedTimeline, key) => {
  const shiftObjs = [];
  let currentShiftObj = {};
  const planStart = sortedTimeline.find((t) => t.key === SSD_TIMELINE_KEY.PLAN && t.type === SSD_TIMELINE_TYPE.START);
  sortedTimeline
    .filter((t) => t.key === key)
    .forEach((shiftObj) => {
      if (shiftObj.type === SSD_TIMELINE_TYPE.START) {
        currentShiftObj.label = shiftObj.label;
        currentShiftObj.startDateTimeLocal = shiftObj.date_time_local;
        currentShiftObj.startTimestamp = shiftObj.timestamp;
      } else {
        currentShiftObj.endDateTimeLocal = shiftObj.date_time_local;
        currentShiftObj.endTimestamp = shiftObj.timestamp;
        shiftObjs.push(currentShiftObj);
        currentShiftObj = {};
      }
    });
  return shiftObjs.filter((shiftObj) => shiftObj.endTimestamp > planStart.timestamp);
};

export const displayTime = (dateTime) => {
  const time = dateTime.split('T')[1];
  const timeArray = time.split(TIME_DELIMITER);
  return `${timeArray[0]}${TIME_DELIMITER}${timeArray[1]}`;
};

export const displayDate = (dateTime) => {
  return dateTime.split('T')[0];
};

export const displayHc = (headcountAllocation) => {
  const hc = chainWalk(() => headcountAllocation.total_hc.toFixed(0), '');
  const newHireHc = chainWalk(() => headcountAllocation.new_hire_hc.toFixed(0), '');

  if (hc > 0 && newHireHc > 0) {
    return hc + ' (' + (hc - newHireHc) + ' + ' + newHireHc + ')';
  }

  return hc;
};

export const displayThrowRate = (headcountAllocation) => {
  if (headcountAllocation == null) {
    return '';
  }

  const {
    process_path: departmentName,
    total_hc: headcount,
    expected_rate_per_hour: perHourRate
  } = headcountAllocation;
  return departmentName === 'injection' && headcount && perHourRate > 0
    ? ` (${calcRoundedThrowRate(perHourRate)} S/P)`
    : '';
};

const calcRoundedThrowRate = (perHourRate) => {
  const secondsPerPackage = (60 * 60) / perHourRate;
  const [integerStr, decimalStr = '0'] = secondsPerPackage.toString().split('.');
  const integer = parseInt(integerStr, 10);
  const decimalValue = Number('0.' + decimalStr);
  if (decimalValue <= 0.6) {
    return integer;
  } else {
    return integer + 1;
  }
};

export const getFcCode = () => {
  return chainWalk(() => 'S' + getStationCode().toString().substr(1), '');
};

export const getNextSSDShiftStartTimeForDailyShifts = (adminSettings) => {
  let nextShiftStartTime = null;
  if (ADMIN_SETTINGS_DAILY_SHIFTS in adminSettings) {
    const currentTimeEpoch = getCurrentTimeEpochForTimezone(adminSettings['timezone']);
    const shifts = adminSettings[ADMIN_SETTINGS_DAILY_SHIFTS];
    const { timezone = moment.tz.guess() } = adminSettings;
    const currentMoment = moment.unix(currentTimeEpoch).tz(timezone);
    const dayOfWeek = currentMoment.format('ddd');
    let currentDate = currentMoment.format('YYYY-MM-DD');
    for (let i = 0; i < DAYS.length; i++) {
      const dayIndex = (DAYS.indexOf(dayOfWeek) + i) % DAYS.length;
      const day = DAYS[dayIndex];
      const shiftsForDay = (JSON.stringify(shifts[day]) === '{}' ? [] : shifts[day] || []).sort(
        (a, b) => getLocalTimeEpoch(timezone, a.start, currentDate) - getLocalTimeEpoch(timezone, b.start, currentDate)
      );

      shiftsForDay.forEach((shift) => {
        const shiftStartTimeEpoch = getLocalTimeEpoch(timezone, shift.start, currentDate);
        if (nextShiftStartTime === null && currentTimeEpoch < shiftStartTimeEpoch) {
          nextShiftStartTime = shiftStartTimeEpoch;
        }
      });
      if (nextShiftStartTime != null) {
        break;
      }
      // Update currentDate for the next day
      currentDate = getNextDate(currentDate);
    }
  }
  return nextShiftStartTime;
};

const getLocalTimeEpoch = (timezone, localTime, currentDate) => {
  const localDateTime = moment.tz(`${currentDate} ${localTime}`, timezone);
  return localDateTime.unix();
};

export const getNextDate = (currentDate) => {
  const currentDateObj = moment(currentDate);
  return currentDateObj.add(1, 'days').format('YYYY-MM-DD');
};

const getCurrentTimeEpochForTimezone = (timezone) => {
  const now = moment.tz(timezone);
  return now.unix();
};

export const getShiftEndTimeForShiftStartTime = (shiftStartTime, timezone = moment.tz.guess()) => {
  return moment.unix(shiftStartTime).tz(timezone).add(1, 'day').unix();
};

export const getNextQuarterLocalDateTime = (adminSettings) => {
  const currentTimeEpoch = getCurrentTimeEpochForTimezone(adminSettings['timezone']);
  const currentMoment = moment.unix(currentTimeEpoch).tz(adminSettings['timezone']);
  const currentLocalTime = currentMoment.format('HH:mm:ss');
  const nextQuarterLocalTime = getNextQuarterLocalTime(currentLocalTime);
  const currentDate = currentMoment.format('YYYY-MM-DD');
  let localDateTime = getLocalTimeEpoch(adminSettings['timezone'], nextQuarterLocalTime, currentDate);
  const currentTime = getCurrentTimeEpochForTimezone(adminSettings['timezone']);
  if (currentTime > localDateTime) {
    localDateTime = moment.unix(localDateTime).tz(adminSettings['timezone']).add(1, 'day').unix();
  }
  return localDateTime;
};

/**
 * Checks whether a given feature flag is enabled from the admin settings
 * @param {*} adminSettings
 * @param {*} featureFlagName
 * @param {*} defaultValue
 * @returns
 */
export const isFeatureFlagEnabled = (adminSettings, featureFlagName, defaultValue = false) => {
  return chainWalk(() => JSON.parse(adminSettings)[featureFlagName], defaultValue);
};

export const displayBusinessTabHeader = (selectedBusiness) => {
  if (!selectedBusiness) return CORE_BUSINESS_TYPE_LABEL;
  if (selectedBusiness === BUSINESS_VIEW_NAME.CORE) return CORE_BUSINESS_TYPE_LABEL;

  return trimAndCapitalizeFirstLetter(selectedBusiness);
};

const createRoleToBusinessDict = (plan) => {
  const roleToBusinessDict = {};

  const roleBusinessMapping = chainWalk(
    () => JSON.parse(plan.output.serializedOutput).transformedInputs.role_to_business_mapping,
    []
  );
  roleBusinessMapping.forEach((entry) => {
    const processPath = entry.process_path || '';
    const process = entry.process || 'nan';
    const processRole = entry.process_role || '';
    const business = entry.business_type || BUSINESS_VIEW_NAME.CORE;

    const processRoleID = `${processPath}__${process}__${processRole}`;
    roleToBusinessDict[processRoleID] = business === '' || !business ? BUSINESS_VIEW_NAME.CORE : business.toLowerCase();
  });

  return roleToBusinessDict;
};

// initialized when needed with the plan parameter
let roleToBusinessDict = null;

/**
 * Creates a standardized role key for business mapping lookups.
 * Format: processPath__process__processRole
 * @param {string} processPath - The process path (e.g., 'outbound')
 * @param {string} process - The process type (e.g., 'pick', 'pack')
 * @param {string} processRole - The specific role name
 * @returns {string} Formatted role key for business mapping
 */
export const createRoleKey = (processPath, process, processRole) => {
  return `${processPath || ''}__${process || 'nan'}__${processRole || ''}`;
};

export const getBusiness = (role, plan = null) => {
  // initialize roleToBusinessDict if it's null and plan provided
  if (roleToBusinessDict === null && plan) {
    roleToBusinessDict = createRoleToBusinessDict(plan);
  }

  return roleToBusinessDict && roleToBusinessDict[role] ? roleToBusinessDict[role] : null;
};

/**
 * Extracts the role name from a role key by removing department prefixes and handling special formats
 * @param {string} roleKey - The role key to extract the name from
 * @returns {string} The extracted role name
 */
export const extractRoleNameFromKey = (roleKey) => {
  // Extract role name from the key
  return roleKey.includes('__') ? roleKey.split('__').pop() : roleKey.replace(/^(outbound|sortation|injection)_/, '');
};
